// ────────────────────────────────────────────────────────────────────
// ⚠️ TIER-2 CLEANUP (VIN-2026-27, 2026-05-26) — wines.userId → cellarId
// cutover deferred to production migration.
//
// Wine writes still stamp BOTH `cellarId` and `userId` for back-compat
// with the 20+ read sites that still query `where('userId', '==', uid)`.
// Cutover requires migrating every reader atomically OR running a
// transitional period where readers fall back to cellarId.
//
// PRODUCTION-MIGRATION CHECKLIST:
//   1. Audit every `where('userId', '==', ...)` against the wines
//      collection (`rg "wines.*where\\('userId'"` finds them).
//   2. Switch each to `where('cellarId', '==', ...)` after confirming the
//      reader has access to the right cellarId.
//   3. Drop the `userId` write in addWine + EditWine paths.
//   4. Drop the redundant `userId+createdAt` composite index in
//      firestore.indexes.json.
//   5. Update firestore.rules `wines/` block to validate cellarId-only.
// ────────────────────────────────────────────────────────────────────
//
// cellars.js — helpers for the cellars/{cellarId} collection.
//
// Background: pre-migration the `wines` collection was keyed by uid (every
// wine had `userId: <owner>`). To support household members sharing one
// logical cellar without cloning rows, we introduced `cellars/{cellarId}`.
// Every user has one personal cellar; household members share a single
// household cellar (members[] holds their uids).
//
// During the migration window:
//   - Wine writes stamp BOTH `cellarId` and `userId` so legacy reads still
//     work and new reads can flip cleanly when the cutover lands.
//   - Reads still use `userId` (Phase 1 = additive only). Phase 2 flips
//     reads to cellarId.
//   - `ensureCellarForUser()` is safe to call on every login; it's idempotent
//     and creates a personal cellar lazily for users who don't have one yet.
//
// See docs/design/household-merge-cellars.md for the full spec.

import {
  doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp,
  collection, query, where, getDocs, writeBatch,
  arrayUnion, arrayRemove,
} from 'firebase/firestore'
import { db } from '../firebase.js'
import { withTimeout } from './withTimeout.js'

// Cache the current user's cellarId in-memory so repeated wine writes
// during a session don't each round-trip to Firestore for the profile
// doc. Cleared on signout via clearCellarCache().
let _cachedCellarId = null
let _cachedForUid   = null

export function clearCellarCache() {
  _cachedCellarId = null
  _cachedForUid   = null
}

/**
 * Ensure the given user has a personal cellar doc. Returns the cellarId.
 *
 * Idempotent — safe to call on every login. If the profile already has a
 * cellarId, returns it unchanged. Otherwise creates `cellars/{newId}`,
 * stamps it back onto `profiles/{uid}.cellarId`, and returns the new id.
 *
 * Pass `user` (auth currentUser-style object). Reads `profiles/{uid}` once;
 * if you've already loaded the profile, prefer `ensureCellarForProfile()`
 * to skip the round-trip.
 */
export async function ensureCellarForUser(user) {
  if (!user?.uid) throw new Error('ensureCellarForUser requires a user with uid')
  if (_cachedForUid === user.uid && _cachedCellarId) return _cachedCellarId

  // 2026-05-25: wait for the auth token to be ready before reading
  // the profile — same race as App.jsx's onAuthStateChanged. Without
  // this, on a fresh iOS install the getDoc can come back empty
  // because the Firestore client hasn't attached the ID token yet,
  // and we'd fall through to creating a new cellar that orphans the
  // user's wines. getIdToken() resolves once the token is genuinely
  // available; force=false uses the cached token when valid.
  //
  // VIN-2025-08 (2026-05-26): wrap with a 10s timeout. Firebase Auth's
  // first token refresh CAN hang on a stale persisted token + flaky
  // network — without this the entire cellar resolution blocks
  // indefinitely. The continuation below tolerates a missing token
  // (the Firestore read still attempts), so timing out cleanly is
  // preferable to a frozen UI.
  try {
    await withTimeout(user.getIdToken(), 10000, 'cellars-getIdToken')
  } catch (tokErr) {
    console.warn('[ensureCellarForUser] getIdToken pre-fetch failed (continuing):', tokErr?.code || tokErr?.message)
  }

  const profileRef = doc(db, 'profiles', user.uid)
  const snap       = await getDoc(profileRef)
  const existing   = snap.exists() ? snap.data().cellarId : null
  if (existing) {
    // Audit JC-P1.c bidirectional follow-up (2026-05-14): verify the
    // existing cellar is actually accessible before trusting it. The
    // user could have been removed from a household cellar by the
    // OTHER party (disconnect from their side), leaving profile.cellarId
    // pointing at a cellar they can no longer read. Without this
    // check, every subsequent Home query returns zero results
    // ("empty cellar" UX) until the user reinstalls or signs out.
    //
    // Distinguishing cases:
    //  - cellar exists + user is a member → return existing (happy path)
    //  - cellar exists + user is NOT a member → they were removed,
    //                                            spin up a fresh
    //                                            personal cellar
    //  - cellar doc missing → deleted upstream, recreate
    //  - network/transient error → trust the stored id (don't recreate;
    //                              next session will retry)
    let needsRecreate = false
    try {
      const cellarSnap = await getDoc(doc(db, 'cellars', existing))
      if (!cellarSnap.exists()) {
        needsRecreate = true
      } else {
        const data    = cellarSnap.data()
        const members = data.members || []
        const isMember = members.includes(user.uid)
        const isOwner  = data.primaryOwner === user.uid

        if (!isMember && !isOwner) {
          // Truly not associated — removed from a household by the other
          // member. Spin up a fresh personal cellar.
          needsRecreate = true
        } else if (isOwner && !isMember) {
          // Legacy data: cellar created before `members` was the auth
          // primitive. User is the rightful owner but isn't in the
          // members array, which makes Firestore rules block reads from
          // this same path. Backfill members so the next session is
          // clean — and our own reads (wines query, etc.) start
          // working. Best-effort: a write failure here is non-fatal
          // because the owner check kept us authorized.
          //
          // Surfaced 2026-05-25 on Nick's account after iOS reinstall —
          // his legacy cellar (5ac41c5c-…) was reachable via wine
          // userId path but cellar getDoc was failing for the SAME
          // reason, triggering recreate on every sign-in.
          try {
            await updateDoc(doc(db, 'cellars', existing), {
              members: arrayUnion(user.uid),
              updatedAt: serverTimestamp(),
            })
            console.info('[ensureCellarForUser] backfilled members for legacy cellar', existing)
          } catch (writeErr) {
            console.warn('[ensureCellarForUser] members backfill failed (non-fatal):', writeErr?.code || writeErr?.message)
          }
        }
      }
    } catch (err) {
      // permission-denied here means the cellar exists but neither
      // members nor primaryOwner check would pass for the current rules
      // — i.e., we were genuinely removed by another household member,
      // OR the cellar pre-dates both fields. Without server-side admin
      // SDK access we can't distinguish these from the client, so we
      // fall back to creating a fresh personal cellar. The original
      // cellar (and any wines tagged with its id via the legacy userId
      // path) remain readable via the wines collection's userId
      // fallback rule, just not via the cellar-membership read path.
      //
      // Recovery for users hitting this on legacy cellars: an admin
      // (Firestore Console) needs to add the user's uid to the cellar's
      // members array OR add a primaryOwner field. See the rules
      // update at firestore.rules that allows primaryOwner-based reads,
      // which is how this code path becomes self-healing once deployed.
      if (err?.code === 'permission-denied') {
        needsRecreate = true
      } else {
        console.warn('[ensureCellarForUser] cellar verification failed (non-fatal):', err?.code || err?.message)
      }
    }

    if (!needsRecreate) {
      _cachedCellarId = existing
      _cachedForUid   = user.uid
      return existing
    }
    // Fall through to the create path below.
  }

  // 2026-05-25: defensive re-read before the create path. The user
  // reported losing their cellar AGAIN on app update after the
  // ensureCellarForProfile defensive fix — so the race is firing on
  // this code path too (called from dupeCount.js, getCellarIdForWriting,
  // and several pages during init). If the user has an EXISTING profile
  // (snap.exists() == true), we never auto-create a new cellar — better
  // to return null and let the caller surface a temporary loading
  // state than to orphan their wines under a discarded cellarId. The
  // 'existing profile but no cellarId' state is recoverable (next
  // session will resolve) but a cellarId overwrite isn't.
  if (snap.exists()) {
    console.warn('[ensureCellarForUser] profile exists but no cellarId — refusing to auto-create. Caller should retry or surface loading state.')
    // Do NOT cache null (don't poison the cache); just return.
    return null
  }

  // Truly no profile yet — genuine new-user signup path. Safe to create.
  const cellarId = newCellarId()
  // Create the cellar doc + stamp the profile in parallel. If the profile
  // doc doesn't exist yet (very first sign-in race), setDoc with merge
  // creates it; the auth listener will fill in the rest of the fields.
  const cellarRef = doc(db, 'cellars', cellarId)
  await Promise.all([
    setDoc(cellarRef, {
      type:         'personal',
      members:      [user.uid],
      primaryOwner: user.uid,
      createdAt:    serverTimestamp(),
      updatedAt:    serverTimestamp(),
    }),
    setDoc(profileRef, { cellarId }, { merge: true }),
  ])

  _cachedCellarId = cellarId
  _cachedForUid   = user.uid
  return cellarId
}

/**
 * Variant for callers that have already loaded the profile snapshot —
 * avoids a redundant getDoc on the happy path. If the profile lacks a
 * cellarId, creates one and stamps the profile.
 *
 * Defensive re-read added 2026-05-25: on iOS fresh-install + first
 * sign-in, App.jsx's getDoc(profileRef) was returning a profileData
 * snapshot that LACKED cellarId — even though the profile in Firestore
 * had one. The cause is most likely a transient auth-token / SDK race
 * specific to Capacitor's first-launch path (we couldn't fully isolate
 * it without device-side logging, but the pattern was: fresh install →
 * profile.cellarId overwritten with new UUID → wines stranded under
 * original cellarId until manual Firestore Console fix). Symptom:
 * "I lost my cellar after reinstalling" — every single iOS install.
 *
 * Fix: before committing to the create path, do a fresh getDoc.
 * Adds one Firestore read on the cold path (when the caller-supplied
 * profileData was missing cellarId), zero overhead on the warm path
 * (happy case bails before this).
 */
export async function ensureCellarForProfile(uid, profileData) {
  if (!uid) throw new Error('ensureCellarForProfile requires uid')
  if (_cachedForUid === uid && _cachedCellarId) return _cachedCellarId

  const existing = profileData?.cellarId
  if (existing) {
    _cachedCellarId = existing
    _cachedForUid   = uid
    return existing
  }

  // profileData says no cellarId, but caller may have read a stale
  // profile snapshot (fresh-install race). Re-read directly before
  // committing to creating a new cellar — destroying a user's cellarId
  // mapping is a high-cost mistake.
  try {
    const freshSnap = await getDoc(doc(db, 'profiles', uid))
    const freshCellarId = freshSnap.exists() ? freshSnap.data()?.cellarId : null
    if (freshCellarId) {
      console.info('[ensureCellarForProfile] using fresh profile cellarId; caller-supplied profileData was stale')
      _cachedCellarId = freshCellarId
      _cachedForUid   = uid
      return freshCellarId
    }
    // 2026-05-25 hardening: if profile EXISTS but has no cellarId
    // (the fresh re-read confirms), DO NOT auto-create. The user
    // reported losing wines again after the original defensive fix,
    // meaning the auto-create here was still firing for existing
    // profiles. Match ensureCellarForUser's refusal pattern — return
    // null and let the caller surface a loading state. Genuine
    // new-user accounts (where profileSnap.exists() is false) still
    // get auto-creation below.
    if (freshSnap.exists()) {
      console.warn('[ensureCellarForProfile] profile exists but no cellarId — refusing to auto-create. Will retry next session.')
      return null
    }
  } catch (err) {
    // If the fresh read fails (offline, transient permission lag during
    // first-launch auth bootstrap), don't trust our negative finding —
    // bail out without writing. The next session will retry; we'd
    // rather a temporary "cellar not loaded yet" state than a permanent
    // cellarId overwrite.
    console.warn('[ensureCellarForProfile] fresh re-read failed, skipping create to avoid clobber:', err?.code || err?.message)
    return null
  }

  // True new-user path — profile doc does not exist. Safe to create.
  const cellarId  = newCellarId()
  const cellarRef = doc(db, 'cellars', cellarId)
  const profileRef = doc(db, 'profiles', uid)
  await Promise.all([
    setDoc(cellarRef, {
      type:         'personal',
      members:      [uid],
      primaryOwner: uid,
      createdAt:    serverTimestamp(),
      updatedAt:    serverTimestamp(),
    }),
    setDoc(profileRef, { cellarId }, { merge: true }),
  ])

  _cachedCellarId = cellarId
  _cachedForUid   = uid
  return cellarId
}

/**
 * Lookup the cellar doc for a given cellarId. Returns the data + id, or
 * null if the doc doesn't exist (e.g. legacy user pre-migration).
 */
export async function loadCellar(cellarId) {
  if (!cellarId) return null
  const snap = await getDoc(doc(db, 'cellars', cellarId))
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

/**
 * Sentinel error code for the "profile exists but cellarId is not yet
 * resolved" race. Thrown synchronously by getCellarIdForWriting so
 * every wine-write call site can catch + surface a retry-friendly
 * toast instead of silently orphaning the wine with cellarId: null.
 *
 * 2026-05-25 (VIN-2025-01): ensureCellarForUser was hardened to REFUSE
 * to auto-create a cellar when the profile doc exists (data-preservation
 * over auto-recovery). Hardening was correct on the READ side — but write
 * sites uniformly passed the null straight to addDoc, producing wines
 * with cellarId: null that became invisible to every cellarId-keyed
 * query. This sentinel makes the failure mode loud + recoverable: each
 * caller catches CELLAR_NOT_READY and toasts "Setting up your cellar —
 * try again in a sec." The error is intentionally typed (not a generic
 * Error) so call sites can branch on err.code === 'CELLAR_NOT_READY'.
 */
export const CELLAR_NOT_READY = 'CELLAR_NOT_READY'

export class CellarNotReadyError extends Error {
  constructor(message = 'Cellar is being set up — try again in a moment.') {
    super(message)
    this.name = 'CellarNotReadyError'
    this.code = CELLAR_NOT_READY
  }
}

/**
 * Resolve the cellarId a wine write should stamp. If the user's profile
 * has a cellarId, use it. Otherwise create one and use it. Cached so
 * repeated calls during a session are O(1).
 *
 * Throws CellarNotReadyError (code: 'CELLAR_NOT_READY') when the cellar
 * isn't available yet. Call sites must catch + surface a retry-friendly
 * toast — see VIN-2025-01 comment above.
 */
export async function getCellarIdForWriting(user) {
  const id = await ensureCellarForUser(user)
  if (!id) throw new CellarNotReadyError()
  return id
}

/**
 * Add a member to a household cellar. If the cellar is currently
 * 'personal', it gets promoted to 'household' on first add.
 *
 * Used by the household-invite acceptance flow: when uid2 accepts an
 * invite from uid1, we add uid2 to uid1's cellar's members array, mark
 * the cellar as type='household', and update uid2's profile.cellarId
 * to point to the shared cellar (deleting their now-orphan personal
 * cellar happens elsewhere — see the migration script for how legacy
 * household connections are consolidated).
 *
 * Optional `inviterDisplayName` (2026-05-18, locked-in decision #4):
 * if the cellar is about to be promoted from personal → household for
 * the first time AND currently has no name, auto-derive "The
 * {LastName} Cellar" from the inviter's displayName. Fallback to
 * "{FirstName}'s Cellar" when there's no obvious last name (single-
 * token display name, etc). User can rename anytime via the Sharing
 * page's inline editor.
 */
export async function addMemberToCellar(cellarId, newMemberUid, inviterDisplayName = null) {
  if (!cellarId || !newMemberUid) throw new Error('cellarId + newMemberUid required')
  const ref = doc(db, 'cellars', cellarId)
  // Audit JC-P1.b (2026-05-14): switched to arrayUnion. Pre-fix this
  // did getDoc → spread + write, which raced under concurrent invite
  // acceptances: two simultaneous adds both read the same baseline
  // and the second write clobbered the first. arrayUnion is atomic
  // server-side. We still want to verify the doc exists (for the
  // "Cellar X does not exist" error semantics callers depend on) so
  // the existence check stays — and we need it for the auto-name
  // promotion check below.
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error(`Cellar ${cellarId} does not exist`)

  const data = snap.data() || {}
  const wasPersonal = data.type === 'personal'
  const hasNoName   = !data.name || !String(data.name).trim()

  const patch = {
    type:    'household',
    members: arrayUnion(newMemberUid),
    updatedAt: serverTimestamp(),
  }

  // Auto-derived name on first promotion — only when there's no name
  // yet. Subsequent member adds + later renames don't touch it.
  if (wasPersonal && hasNoName && inviterDisplayName) {
    const derived = deriveHouseholdName(inviterDisplayName)
    if (derived) patch.name = derived
  }

  await updateDoc(ref, patch)
}

/**
 * "The {LastName} Cellar" auto-derivation for the household promotion
 * step. Returns null when the input is empty or whitespace-only.
 *
 * Rules:
 *   - Split on whitespace, drop empty tokens
 *   - 0 tokens (empty/whitespace) → null
 *   - 1 token → "{token}'s Cellar"  (no obvious last name; possessive form)
 *   - 2+ tokens → "The {lastToken} Cellar"  (uses surname)
 *
 * Single-name users (one-word display names) get the possessive form
 * because "The Cher Cellar" reads awkwardly compared to "Cher's Cellar".
 */
export function deriveHouseholdName(displayName) {
  const tokens = String(displayName || '').trim().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return null
  if (tokens.length === 1) return `${tokens[0]}'s Cellar`
  return `The ${tokens[tokens.length - 1]} Cellar`
}

/**
 * Remove a member from a household cellar. Used by:
 *   - User leaves a household (self-remove)
 *   - User account deletion (cascade removes them from any household
 *     cellar they were a member of)
 * The wines stay in the shared cellar — the leaving member loses access
 * but doesn't take "their" wines with them. See spec §11.5.
 */
export async function removeMemberFromCellar(cellarId, memberUid) {
  if (!cellarId || !memberUid) throw new Error('cellarId + memberUid required')
  const ref = doc(db, 'cellars', cellarId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return
  const data = snap.data()
  if (!data.members?.includes(memberUid)) return  // wasn't a member
  // Audit JC-P1.b (2026-05-14): use arrayRemove for the members
  // write so concurrent removes don't clobber each other. The
  // type-flip-to-personal still depends on the snapshot read, but
  // that's a derived ui semantic — a small race window here just
  // means a one-member cellar might briefly read as 'household';
  // self-corrects on next write.
  const wouldBeMembersLen = (data.members || []).length - 1
  await updateDoc(ref, {
    members: arrayRemove(memberUid),
    updatedAt: serverTimestamp(),
    // If only one member left, demote back to personal — keeps the
    // semantic clean and unlocks the personal-cellar single-member rule.
    ...(wouldBeMembersLen === 1 ? { type: 'personal' } : {}),
  })
}

/**
 * Spin up a fresh personal cellar for a user who's just been removed
 * from a household cellar. Without this, `profile.cellarId` keeps
 * pointing at the cellar they no longer have access to → Home query
 * returns zero results → "empty cellar" UX even though they own
 * wines (see audit JC-P1.c, 2026-05-14).
 *
 * Behavior:
 *   - Creates a brand-new cellars/{newId} with the user as primary owner.
 *   - Atomically updates profiles/{uid}.cellarId to point at it.
 *   - Clears the module-level cache so the next ensureCellarForUser()
 *     call sees the new id.
 *
 * Idempotent on retry — if profile.cellarId was already updated by an
 * earlier attempt, this still creates a NEW cellar and stamps that.
 * Callers should not retry blindly; treat the returned cellarId as
 * authoritative.
 *
 * Wines stay in the household cellar — see spec §11.5: the leaver
 * doesn't take "their" wines with them. This helper only restores
 * write access to a personal namespace; the new cellar starts empty.
 */
export async function recreatePersonalCellarForUser(user) {
  if (!user?.uid) throw new Error('recreatePersonalCellarForUser requires user')
  const cellarId = newCellarId()
  const cellarRef = doc(db, 'cellars', cellarId)
  const profileRef = doc(db, 'profiles', user.uid)
  await Promise.all([
    setDoc(cellarRef, {
      type:         'personal',
      members:      [user.uid],
      primaryOwner: user.uid,
      createdAt:    serverTimestamp(),
      updatedAt:    serverTimestamp(),
    }),
    setDoc(profileRef, { cellarId }, { merge: true }),
  ])
  // Clear cache so subsequent ensureCellarForUser() sees the new id.
  _cachedCellarId = cellarId
  _cachedForUid   = user.uid
  return cellarId
}

/**
 * Apply a merge decision for one duplicate pair in a household cellar.
 *
 * Decision values:
 *   'keep-mine'   → delete the partner's copy; my wine survives unchanged
 *   'keep-theirs' → delete my copy; partner's wine survives unchanged
 *   'keep-both'   → no-op; both rows remain (user decided they aren't dupes)
 *
 * Phase 3.0 keeps this simple: the surviving wine is preserved as-is,
 * the loser is deleted. Per-field merge ("keep my name + their tasting
 * notes") is deferred to a Phase 3.1 follow-up. Ratings on the loser
 * become orphan rows — fine for now since RatingsAggregate writes are
 * keyed by wineId and won't include them. A future cleanup pass can
 * either migrate ratings to the survivor or sweep orphans.
 *
 * @returns { kept: <wineId>, deleted: <wineId | null> }
 */
export async function applyMergeDecision({ mine, theirs, decision }) {
  if (!mine?.id || !theirs?.id) throw new Error('applyMergeDecision requires both wines')
  if (decision === 'keep-both') {
    return { kept: null, deleted: null }
  }
  const survivor = decision === 'keep-mine' ? mine : theirs
  const loser    = decision === 'keep-mine' ? theirs : mine
  await deleteDoc(doc(db, 'wines', loser.id))
  return { kept: survivor.id, deleted: loser.id }
}

/**
 * Stamp the connection doc as merge-completed so the Sharing UI hides
 * the "Merge cellars" CTA after the user finishes their review pass.
 * Even if some pairs were marked keep-both (still duplicate-ish), the
 * user has reviewed every pair so we shouldn't keep nagging them.
 */
export async function markMergeCompleted(connectionId) {
  if (!connectionId) return
  await updateDoc(doc(db, 'connections', connectionId), {
    mergeCompletedAt: serverTimestamp(),
  })
}

// Generate a stable cellar id. crypto.randomUUID() is on every modern
// browser + Node 19+. We avoid using the wineId pattern (autoId from
// Firestore) so a cellarId can be stamped onto wine docs at write time
// without a server round-trip to learn its value.
function newCellarId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for environments without randomUUID (older Node, certain
  // test runners). Not cryptographically strong but collision-resistant
  // enough for cellar ids in our scale.
  return 'c_' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36)
}
